import React, { useState, useEffect, useRef, useCallback } from "react";
import CollapsibleJson from "./CollapsibleJson";
import { FiLogOut } from "react-icons/fi";
import { useAuth } from "../helpers/UseAuth";

const MainContent = (props) => {
  const { data, selectedIndex, searchTerm, fetchData } = props
  const [isPaused] = useState(true)
  const [, setIsHardPulse] = useState(false)
  const tickRef = useRef(0)
  const intervalRef = useRef(null)
  const { logout } = useAuth()

  const handleRefresh = useCallback(async () => {
    try {
      if (fetchData) {
        await fetchData()
      }
    } catch (e) {
      console.error("Refresh failed", e)
    }
  }, [fetchData]);


  // Auto-refresh logic with visual pulse
  useEffect(() => {
    // cleanup existing
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    tickRef.current = 0
    setIsHardPulse(false)

    if (!isPaused) {
      // Tick every second to drive pulse and trigger fetch every 2nd second
      intervalRef.current = setInterval(async () => {
        const next = (tickRef.current + 1) % 2
        tickRef.current = next
        if (next === 0) {
          // 2nd second: trigger hard pulse and fetch
          setIsHardPulse(true)
          try {
            await handleRefresh()
          } finally {
            // remove hard pulse shortly after to create a distinct bump
            setTimeout(() => setIsHardPulse(false), 300)
          }
        }
      }, 1000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isPaused, handleRefresh])

  // Prepare body data: get all properties except `entireEvent` and also extract `entireEvent`
  const selectedItem = (Array.isArray(data) && Number.isInteger(selectedIndex) && selectedIndex >= 0 && selectedIndex < data.length)
    ? data[selectedIndex]
    : null
  const rawBody = selectedItem && selectedItem.body !== undefined ? selectedItem.body : {}
  let bodyObj = rawBody
  if (typeof rawBody === 'string') {
    try {
      bodyObj = JSON.parse(rawBody)
    } catch (_) {
      bodyObj = {}
    }
  }
  const { entireEvent, usedWebhookFilter, ...filteredBody } = (bodyObj && typeof bodyObj === 'object' && bodyObj !== null) ? bodyObj : {}
  // Split filteredBody into bubble-specific keys (starting with "bubble_") and the rest
  const bubbleBody = {}
  const restBody = {}
  if (filteredBody && typeof filteredBody === 'object') {
    Object.keys(filteredBody).forEach((key) => {
      if (key.startsWith('bubble_')) {
        bubbleBody[key] = filteredBody[key]
      } else {
        restBody[key] = filteredBody[key]
      }
    })
  }
  // Normalize Bubble keys (remove prefix) and enforce required ordering
  const bubbleKeyMap = {
    bubble_client_id: 'client_id',
    bubble_type: 'type',
    bubble_id: 'id',
    bubble_direction: 'direction',
    bubble_state: 'state',
    bubble_number: 'number',
    bubble_external_number: 'external_number',
    bubble_user_extension: 'user_extension',
  }
  const bubbleOrder = [
    'client_id',
    'type',
    'id',
    'direction',
    'state',
    'number',
    'external_number',
    'user_extension',
  ]
  const normalizedBubble = {}
  // strip prefix using the mapping above, ignore unknown keys not in the map
  Object.keys(bubbleBody).forEach((key) => {
    const newKey = bubbleKeyMap[key]
    if (newKey) {
      normalizedBubble[newKey] = bubbleBody[key]
    }
  })
  const orderedBubbleBody = {}
  bubbleOrder.forEach((k) => {
    if (normalizedBubble[k] !== undefined) {
      orderedBubbleBody[k] = normalizedBubble[k]
    }
  })
  const headersSafe = (selectedItem && selectedItem.headers) ? selectedItem.headers : {}

  const copyToClipboard = async (obj) => {
    try {
      const text = JSON.stringify(obj ?? {}, null, 2)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'absolute'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
    } catch (e) {
      console.error('Copy failed', e)
    }
  }

  return (
    <div className="main-content">
      <div className="controls" style={{ justifyContent: 'flex-end' }}>
        <div className="controls-right">
          <div className="user-label" title={localStorage.getItem('username') || ''}>
            {localStorage.getItem('username') || ''}
          </div>
          <button onClick={logout} title="Logout">
            <FiLogOut size={18} />
          </button>
        </div>
      </div>
      {!props.message ? (
        (Array.isArray(data) && data.length > 0 && selectedItem) ? (
          <>
            <p className="section-title">
              Webhook Header
              <span
                className="info-icon"
                title="What integrators receive in the webhook request header"
                aria-label="Info"
                role="img"
              >ⓘ</span>
            </p>
            <CollapsibleJson
              data={headersSafe}
              highlightTerm={searchTerm}
              onCopy={() => copyToClipboard(headersSafe)}
            />
            <p className="section-title">
              Webhook Body
              <span
                className="info-icon"
                title="What integrators receive in the webhook request body"
                aria-label="Info"
                role="img"
              >ⓘ</span>
            </p>
            <CollapsibleJson
              data={restBody}
              highlightTerm={searchTerm}
              onCopy={() => copyToClipboard(restBody)}
            />
            <p className="section-title">
              Bubble Webhook Body
              <span
                className="info-icon"
                title="Bubble-specific parameters (without 'bubble_' prefix) in required order"
                aria-label="Info"
                role="img"
              >ⓘ</span>
            </p>
            <CollapsibleJson
              data={orderedBubbleBody}
              highlightTerm={searchTerm}
              onCopy={() => copyToClipboard(orderedBubbleBody)}
            />
            <p className="section-title">
              Webhook Filter
              <span
                className="info-icon"
                title="Events that pass the filter and are sent to the integrator will be highlighted more prominently in the sidebar."
                aria-label="Info"
                role="img"
              >ⓘ</span>
            </p>
            <CollapsibleJson
              data={(usedWebhookFilter && usedWebhookFilter.usedFilter) || []}
              highlightTerm={searchTerm}
              onCopy={() => copyToClipboard((usedWebhookFilter && usedWebhookFilter.usedFilter) || [])}
            />
            <p className="section-title">Full FS Event</p>
            <CollapsibleJson
              data={entireEvent || {}}
              highlightTerm={searchTerm}
              onCopy={() => copyToClipboard(entireEvent || {})}
            />
          </>
        ) : (
          "No data to render"
        )
      ) : (
        <>{props.message}</>
      )}
    </div>
  );
};

export default MainContent;
